import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { settingsActions } from 'saur-viseau-state-management/lib/settings/state';
import { isFeatureEnabled } from 'saur-viseau-state-management/lib/settings/selectors';
import { States as SettingsStates } from 'saur-viseau-state-management/lib';
import { handleLogout } from '../LogoutHelper';
import { getAuthProvider, refreshAccessToken } from './authProvider';
import { getLastActivityTime, getInactivityThresholdMs } from './useUserActivity';

/**
 * Hook de sécurité : vérifie à chaque rendu si le token est expiré.
 * Si expiré ET utilisateur actif (moins de exp-iat depuis le dernier clic),
 * tente un rafraîchissement. Sinon déconnecte directement.
 *
 * Note : pour un rafraîchissement proactif (avant expiration),
 * utiliser `useRefreshToken` en complément.
 */
function useExpireToken(logoutPath: string, userType: string | null | undefined) {
    const dispatch = useDispatch();
    const isTokenDebugEnabled = useSelector((state: SettingsStates) =>
        isFeatureEnabled(state, 'Show-Token-Debug')
    );
    const expires_at = JSON.parse(
        localStorage.getItem('sso-response') ?? 'null'
    )?.expires_at;

    useEffect(() => {
        if (expires_at && expires_at * 1000 < new Date().getTime()) {
            const logoutUrl = `${window.location.origin}${logoutPath}`;
            const now = Date.now();
            const timeSinceClick = now - getLastActivityTime();
            const isUserActive = timeSinceClick < getInactivityThresholdMs();

            if (isTokenDebugEnabled) {
                console.group('%c[useExpireToken] Token expiré détecté', 'color: #fb8c00; font-weight: bold');
                console.log('[useExpireToken] expires_at  :', new Date(expires_at * 1000).toLocaleTimeString('fr-FR'));
                console.log('[useExpireToken] Expiré il y a :', `${Math.round((now / 1000) - expires_at)}s`);
                console.log('[useExpireToken] Dernier clic  :', `il y a ${Math.round(timeSinceClick / 1000)}s`);
                console.log('[useExpireToken] Utilisateur actif :', isUserActive ? '✅ OUI → tentative de refresh' : '❌ NON → logout direct');
                const idTokenPreview = JSON.parse(localStorage.getItem('sso-response') ?? 'null')?.id_token;
                console.log('[useExpireToken] id_token (avant action) :', idTokenPreview ? `${idTokenPreview.slice(0, 20)}…` : '⚠️ absent');
                console.groupEnd();
            }

            if (!isUserActive) {
                handleLogout(dispatch, getAuthProvider(), logoutUrl, userType, 'useExpireToken:inactif');
                return;
            }

            refreshAccessToken()
                .then((tokens) => {
                    if (tokens) {
                        dispatch(settingsActions.setAccessToken(tokens.token));
                        setTimeout(() => {
                            dispatch(settingsActions.setRefreshToken(tokens.refreshToken));
                        }, 100);
                    } else {
                        if (isTokenDebugEnabled) console.warn('[useExpireToken] Refresh échoué → logout');
                        handleLogout(dispatch, getAuthProvider(), logoutUrl, userType, 'useExpireToken:refresh-echoue');
                    }
                })
                .catch((err) => {
                    if (isTokenDebugEnabled) console.error('[useExpireToken] Erreur lors du refresh → logout', err);
                    handleLogout(dispatch, getAuthProvider(), logoutUrl, userType, 'useExpireToken:erreur-refresh');
                });
        }
    });
}

export { useExpireToken };
